/**
 * LineChart Component (Final Version)
 * ===================================
 *
 * This component renders line charts with support for:
 * - Single line charts
 * - Multiple line charts (auto-detected from data)
 * - Interactive tooltips using the CustomTooltip component
 * - Evenly distributed grid lines with consistent step values (5, 10, 15, etc.)
 * - Axes starting at (0,0)
 * - Responsive design
 */

import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import styled from "styled-components";
import {
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { useTrackMousePosition } from "../utils/useTrackMousePosition";
import TooltipV2 from "@/components/UI/Tooltip";
import { TooltipContent } from "../CustomTooltip";
import { usePrepareLineData, TooltipLabel } from "./usePrepareLineData";
import { _base_linechart_ui_configs } from "./_base_linechart_ui_configs";
import { generateConsistentTicks } from "./utils";
import { useChartDataProcessing } from "./hooks/useChartDataProcessing";
import { getTimeSeriesTicks } from "./utils/getTimeSeriesTicks";
import { MAX_POINTS } from "./utils/responsivePoints";
import { colors2 } from "oolib";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const LineChartWrapper = styled.div`
  /* Visible line transition - ultra smooth and slow */
  .recharts-line-curve {
    transition:
      stroke-width 1000ms cubic-bezier(0.16, 1, 0.3, 1),
      stroke-opacity 1000ms cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;
  }

  /* Hit-area styling (cursor is set inline per chart: pointer only when
     the lines actually take a click) */
  .line-hitarea .recharts-line-curve {
    pointer-events: auto;
  }

  /* When a hit-area is hovered, make its paired visible line thicker */
  /* Uses adjacent sibling selector: hitarea + visible-line */
  /* Delay before starting the transition, then smooth gradual animation */
  .line-hitarea:hover + .line-visible .recharts-line-curve {
    transition-delay: 500ms;
    transition-duration: 1000ms;
    transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    stroke-width: 3px;
  }

  /* Smooth transition out when hover ends - no delay, immediate smooth animation */
  .line-hitarea:not(:hover) + .line-visible .recharts-line-curve {
    transition-delay: 0ms;
    transition-duration: 1000ms;
    transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

// =============================================================================
// MEMOIZED TOOLTIP CONTENT COMPONENT
// =============================================================================
// This component handles its own mouse tracking to isolate state changes
// and prevent re-renders of the entire chart when mouse position changes.

interface MemoizedTooltipContentProps {
  // Recharts passes these props to content component
  label?: string | number;
  // Our props
  data: any[];
  xAxis: string;
  tooltipData: TooltipLabel[][];
  wrapperRef: React.RefObject<HTMLDivElement>;
}

const MemoizedTooltipContentInner: React.FC<MemoizedTooltipContentProps> = ({
  label,
  data,
  xAxis,
  tooltipData,
  wrapperRef,
}) => {
  // Mouse tracking is isolated here - state changes don't propagate to parent
  const { mousePosition, isHovering } = useTrackMousePosition({
    containerRef: wrapperRef,
  });

  // Don't render tooltip when not hovering
  if (!isHovering || mousePosition === null) {
    return null;
  }

  const dataIndex =
    label !== undefined ? data.findIndex((item) => item[xAxis] === label) : -1;

  if (dataIndex === -1 || !tooltipData[dataIndex]) return null;

  return (
    <TooltipV2
      controlled
      isOpen={isHovering && mousePosition !== null}
      mousePosition={mousePosition}
      primaryContent={{
        layout: 'custom',
        render: (
          <TooltipContent type="line" value={tooltipData[dataIndex]} xAxisLabel={label} />
        ),
      }}
    />
  );
};

const MemoizedTooltipContent = React.memo(MemoizedTooltipContentInner);

const TICK_FONT_FAMILY =
  "'DM Sans', 'Noto Sans Devanagari', 'Noto Sans Gujarati', 'Noto Sans Oriya', 'Noto Sans Telugu', sans-serif";

type XAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: any };
  tickMargin?: number;
  fill?: string;
  formatter?: (value: any) => string; // Optional display transform (raw tick value → label); selection/domain still use the raw value.
};

const XAxisTick: React.FC<XAxisTickProps> = React.memo(
  ({ x = 0, y = 0, payload, tickMargin = 10, fill = colors2.grey50, formatter }) => {
    const value = payload?.value;
    const raw = value === null || value === undefined ? null : String(value);
    const label = raw !== null && formatter ? formatter(value) : raw;

    return (
      <text
        x={x}
        y={y}
        dy={tickMargin}
        textAnchor="middle"
        fill={fill}
        fontFamily={TICK_FONT_FAMILY}
        fontWeight={500}
      >
        {label ?? ""}
      </text>
    );
  }
);

// Debug controls
export interface DebugControls {
  mouseEventDetectorOpacity: number;
  chartWrapperBgColor: string;
}

const _debug_controls: DebugControls = {
  mouseEventDetectorOpacity: 0, // only increase this for debugging
  chartWrapperBgColor: "", // set this to some color value for debugging
};

// =============================================================================
// STABLE STYLE OBJECTS
// =============================================================================
// Defined outside component to avoid creating new object references on every render
// (ESLint: react-perf/jsx-no-new-object-as-prop)

/** Style for invisible hit-area line (for easier hover detection). Pointer
 *  only when the chart has a per-line onClick — otherwise the hand promises
 *  a click that does nothing. */
const clickableHitAreaLineStyle: React.CSSProperties = { cursor: "pointer" };
const inertHitAreaLineStyle: React.CSSProperties = { cursor: "default" };

/** Style for visible line (pointer events disabled, transitions handled by CSS) */
const visibleLineStyle: React.CSSProperties = {
  transition: `stroke-width 1000ms cubic-bezier(0.16, 1, 0.3, 1), stroke-opacity 1000ms cubic-bezier(0.16, 1, 0.3, 1)`,
  pointerEvents: "none",
};

/** Base wrapper style (merged with user-provided style in component) */
const baseWrapperStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  minHeight: _base_linechart_ui_configs.minHeight,
  background: _debug_controls.chartWrapperBgColor,
  position: "relative",
};

export interface LineChartProps {
  data: any[]; // The input data
  valuePath?: string | string[]; // Optional path to value(s) to display - if not provided, will be auto-detected
  xAxisPath?: string; // Optional path to the x-axis value - if not provided, first suitable field is used
  tooltipLabelsMapping?: { [key: string]: string }; // Optional mapping for tooltip labels
  labelPath?: string; // Optional path to label for series
  onClick?: (name: string) => void; // Click handler
  onXClick?: (xValue: string | number) => void; // Chart-level click: the x-axis bucket under the cursor (recharts activeLabel). Fires anywhere on the plot, independent of onClick's per-line handler.
  valueSuffix?: string; // Optional suffix for values (e.g., '%', '$')
  colorIdx?: number; // Starting color index
  height?: number | string; // Optional custom height (px or CSS value like "100%")
  breakdownCategoryName?: string; // Optional name for the breakdown category in tooltip
  gridDensity?: { x: number; y: number }; // Optional custom grid density
  style?: React.CSSProperties; // Optional styling
  // Tick props (independent — neither reads the other)
  smartTicks?: boolean; // Use interval-aware, evenly-spaced x-axis ticks instead of recharts' default start/end labels (default: true). Independent of downsample; falls back to MAX_POINTS ticks when downsample is off.
  downsample?: boolean; // Responsively reduce the NUMBER of x-axis ticks to fit the container width, fewer on narrow screens (default: true). Only meaningful while smartTicks is on. Never touches the plotted data.
  maxPoints?: number; // Explicit maximum number of ticks (overrides responsive calculation)
  interval?: string; // Optional: Time interval for evenly spaced ticks ('hour', 'day', 'week', 'month', 'quarter', 'year')
  xAxisTickFormatter?: (value: any) => string; // Optional display transform for x-axis labels (e.g. "2026-01" → "Jan"). Affects labels only, not tick selection.
  // Responsive props
  responsive?: boolean | { minPoints?: number; maxPoints?: number }; // Responsive behavior: true/false or config object with minPoints/maxPoints
  hideYAxis?: boolean; // Hide Y axis labels and line (default: false)
  hideXAxisLine?: boolean; // Hide X axis line but keep tick labels (default: false)
  hideXAxisTicks?: boolean; // Hide the tick LABELS too — minimal sparkline mode (default: false)
  hideGrid?: boolean; // Drop the CartesianGrid entirely — for sparklines on a tinted ground (default: false)
  hideTopGridLine?: boolean; // Keep the grid but drop the line along the plot's top edge — reads as a border inside a card (default: false)
  highlightStrokeBoost?: number; // Extra stroke width on the highlighted line (default: 1; 0 = same weight as the rest)
  colors?: { [key: string]: string }; // Optional per-line color mapping { valuePath: "#hex" }
  highlightLine?: string; // Optional valuePath to highlight (full opacity); others dimmed
  defaultOpacity?: number; // Base opacity for lines when no highlight active (default: 1)
  margin?: { top?: number; right?: number; bottom?: number; left?: number }; // Override chart margins
  yMax?: number; // Optional fixed top of the y axis (e.g. a 0–10 scale); by default the axis scales to the data
}

export const LineChart: React.FunctionComponent<LineChartProps> = ({
  data: _data,
  valuePath,
  xAxisPath,
  tooltipLabelsMapping,
  labelPath,
  onClick,
  onXClick,
  valueSuffix,
  colorIdx = 0,
  height = _base_linechart_ui_configs.defaultHeight,
  gridDensity = _base_linechart_ui_configs.gridDensity,
  style = {},
  downsample = true,
  smartTicks = true,
  maxPoints,
  interval,
  responsive = true,
  hideYAxis = false,
  hideXAxisLine = false,
  hideXAxisTicks = false,
  hideGrid = false,
  hideTopGridLine = false,
  highlightStrokeBoost = 1,
  xAxisTickFormatter,
  colors: colorOverrides,
  highlightLine,
  defaultOpacity = 1,
  margin: marginOverride,
  yMax,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showActiveDot, setShowActiveDot] = useState(false);
  const [hoveredLineId, setHoveredLineId] = useState<string | null>(null);
  const [dimmingActive, setDimmingActive] = useState(false);

  // Delay dimming other lines to avoid flicker on quick passes
  useEffect(() => {
    if (hoveredLineId === null) {
      setDimmingActive(false);
      return;
    }

    setDimmingActive(false);
    const t = window.setTimeout(() => setDimmingActive(true), 500);
    return () => window.clearTimeout(t);
  }, [hoveredLineId]);

  // Extract responsive config if it's an object
  const responsiveConfig = typeof responsive === "object" ? responsive : {};
  const isResponsiveEnabled = responsive !== false;

  // `downsample` only decides how many ticks to fit responsively → maxXTicks.
  // It never touches the plotted data (processedData === data).
  const { processedData, maxXTicks } = useChartDataProcessing({
    data: _data,
    downsample,
    maxPoints,
    interval,
    xAxisPath,
    responsive: isResponsiveEnabled,
    containerRef: wrapperRef,
    responsiveConfig,
  });

  // Prepare data for the line chart (with auto-detection if paths not provided)
  const { data, dataMaxValue: dataDrivenMax, lines, xAxis, tooltipData } = usePrepareLineData({
    _data: processedData,
    xAxisPath,
    labelPath,
    valuePath,
    tooltipLabelsMapping,
    colorIdx,
    colors: colorOverrides,
  });

  const xTicks = useMemo(() => {
    if (!smartTicks) return undefined;
    if (!xAxis) return undefined;
    if (!data || data.length === 0) return undefined;
    // maxXTicks is undefined when downsample is off — fall back to MAX_POINTS so
    // smart ticks stand on their own, independent of downsample.
    const maxTicks = maxXTicks && maxXTicks > 0 ? maxXTicks : MAX_POINTS;
    return getTimeSeriesTicks(data, { timeField: xAxis, maxTicks, interval });
  }, [smartTicks, maxXTicks, xAxis, data, interval]);

  const xAxisTickElement = useMemo(
    () => <XAxisTick tickMargin={10} fill={_base_linechart_ui_configs.xAxis.tickFill} formatter={xAxisTickFormatter} />,
    [xAxisTickFormatter]
  );

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Memoize yTicks calculation
  // ===========================================================================
  // A fixed scale wins over the data's own max, so a series of charts on the
  // same page can share one axis.
  const dataMaxValue = yMax ?? dataDrivenMax;

  // Only recalculates when dataMaxValue or gridDensity.y changes
  const yTicks = useMemo(
    () => generateConsistentTicks(dataMaxValue, gridDensity.y),
    [dataMaxValue, gridDensity.y]
  );

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Stable tickFormatter function
  // ===========================================================================
  // Avoids creating new function reference on every render
  const tickFormatter = useCallback(
    (value: number) => `${value}${valueSuffix || ""}`,
    [valueSuffix]
  );

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute tooltip content element
  // ===========================================================================
  // Creates a stable React element for the Tooltip content prop.
  // Avoids inline function (ESLint: react-perf/jsx-no-new-function-as-prop).
  // Mouse tracking is handled inside MemoizedTooltipContent to isolate state changes.
  const tooltipContentElement = useMemo(
    () => (
      <MemoizedTooltipContent
        data={data}
        xAxis={xAxis}
        tooltipData={tooltipData}
        wrapperRef={wrapperRef}
      />
    ),
    [data, xAxis, tooltipData, wrapperRef]
  );

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-bound click handlers per line
  // ===========================================================================
  // Creates stable function references for each line, avoiding inline functions
  // in the render loop (ESLint: react-perf/jsx-no-new-function-as-prop)
  // NOTE: Hover state is now handled purely via CSS (see style.scss)
  const lineClickHandlers = useMemo(() => {
    if (!onClick) return null;
    return new Map(lines.map((line) => [line.id, () => onClick(line.dataKey)]));
  }, [lines, onClick]);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute line elements
  // ===========================================================================
  // Line hover is handled via CSS using adjacent sibling selector:
  // .line-hitarea:hover + .line-visible .recharts-line-curve
  const activeDotPropsByLineId = useMemo(() => {
    return new Map(
      lines.map((line) => [
        line.id,
        {
          ..._base_linechart_ui_configs.lineActiveDot,
          fill: line.color,
        },
      ])
    );
  }, [lines]);

  const lineHoverHandlers = useMemo(() => {
    return new Map(
      lines.map((line) => [
        line.id,
        {
          onMouseEnter: () => setHoveredLineId(line.id),
          onMouseLeave: () => setHoveredLineId(null),
        },
      ])
    );
  }, [lines]);

  const lineElements = useMemo(() => {
    const dimOthers = (dimmingActive && hoveredLineId !== null) || !!highlightLine;
    const dimOpacity = 0.2;

    return lines.flatMap((line) => [
      // Invisible hit-area line for easier hover detection
      <Line
        key={`${line.id}-hitarea`}
        className="line-hitarea"
        type="linear"
        dataKey={line.dataKey}
        name={line.name}
        stroke="transparent"
        strokeWidth={_base_linechart_ui_configs.lineHitAreaWidth}
        dot={false}
        activeDot={false}
        isAnimationActive={false}
        connectNulls={true}
        legendType="none"
        style={lineClickHandlers ? clickableHitAreaLineStyle : inertHitAreaLineStyle}
        onClick={lineClickHandlers?.get(line.id)}
        onMouseEnter={lineHoverHandlers.get(line.id)?.onMouseEnter}
        onMouseLeave={lineHoverHandlers.get(line.id)?.onMouseLeave}
      />,
      // Visible line (hover effect via CSS)
      <Line
        key={line.id}
        className="line-visible"
        type="linear"
        dataKey={line.dataKey}
        name={line.name}
        stroke={line.color}
        strokeWidth={_base_linechart_ui_configs.lineStrokeWidth}
        dot={false}
        activeDot={showActiveDot ? activeDotPropsByLineId.get(line.id) : false}
        isAnimationActive={false}
        connectNulls={true}
        style={{
          ...visibleLineStyle,
          strokeOpacity: !dimOthers
            ? defaultOpacity
            : (line.id === hoveredLineId || line.dataKey === highlightLine) ? 1 : dimOpacity,
          strokeWidth: line.dataKey === highlightLine
            ? (_base_linechart_ui_configs.lineStrokeWidth + highlightStrokeBoost)
            : _base_linechart_ui_configs.lineStrokeWidth,
        }}
      />,
    ]);
  }, [
    lines,
    hoveredLineId,
    dimmingActive,
    highlightLine,
    highlightStrokeBoost,
    defaultOpacity,
    lineClickHandlers,
    lineHoverHandlers,
    showActiveDot,
    activeDotPropsByLineId,
  ]);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Memoize wrapper style
  // ===========================================================================
  // Merges base style with user-provided style, only recalculates when style prop changes
  const wrapperStyle = useMemo(
    () => ({ ...baseWrapperStyle, ...style }),
    [style]
  );

  // Handle empty data
  if (data.length === 0) return <div>No data available</div>;

  return (
    <LineChartWrapper className="lineChartWrapper" ref={wrapperRef} style={wrapperStyle}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart
          data={data}
          {...(onXClick
            ? {
                onClick: (st: any) => {
                  if (st && st.activeLabel != null) onXClick(st.activeLabel);
                },
                style: { cursor: "zoom-in" },
              }
            : {})}
          margin={{
            top: marginOverride?.top ?? _base_linechart_ui_configs.marginTop,
            right: marginOverride?.right ?? _base_linechart_ui_configs.marginRight,
            bottom: marginOverride?.bottom ?? _base_linechart_ui_configs.marginBottom,
            left: marginOverride?.left ?? _base_linechart_ui_configs.marginLeft,
          }}
          onMouseEnter={() => {
            // In v3, onMouseMove no longer receives CategoricalChartState (isTooltipActive removed).
            // onMouseEnter/onMouseLeave achieve the same effect: show dot when cursor
            // enters the chart SVG area, hide on leave.
            setShowActiveDot(true);
          }}
          onMouseLeave={() => setShowActiveDot(false)}
        >
          {!hideGrid && (
            <CartesianGrid
              strokeDasharray={_base_linechart_ui_configs.grid.strokeDasharray}
              vertical={_base_linechart_ui_configs.grid.vertical}
              horizontal={_base_linechart_ui_configs.grid.horizontal}
              stroke={_base_linechart_ui_configs.grid.stroke}
              {...(hideTopGridLine
                ? {
                    // Our own y ticks through the axis scale, minus any line sitting
                    // on the plot's top edge.
                    horizontalCoordinatesGenerator: ({ yAxis, offset }: any) =>
                      typeof yAxis?.scale === "function"
                        ? yTicks.map((t: number) => yAxis.scale(t)).filter((y: number) => y > offset.top + 1)
                        : [],
                  }
                : {})}
            />
          )}

          <XAxis
            dataKey={xAxis}
            tickLine={_base_linechart_ui_configs.xAxis.tickLine}
            axisLine={
              hideXAxisLine ? false
                : _base_linechart_ui_configs.xAxis.axisLine
                  ? { stroke: _base_linechart_ui_configs.xAxis.axisLineStroke }
                  : false
            }
            padding={_base_linechart_ui_configs.xAxis.padding}
            ticks={smartTicks ? xTicks : undefined}
            interval={smartTicks ? 0 : "preserveStartEnd"}
            tick={hideXAxisTicks ? false : xAxisTickElement}
            // recharts reserves the axis band (default 30px) even with
            // tick={false} — reclaim it in sparkline mode only.
            height={hideXAxisTicks ? 0 : undefined}
            allowDecimals={false}
            fontFamily={TICK_FONT_FAMILY}
            fontWeight={500}
            tickMargin={10}
            stroke={_base_linechart_ui_configs.xAxis.tickFill}
          />

          <YAxis
            domain={[0, dataMaxValue]}
            tickLine={hideYAxis ? false : _base_linechart_ui_configs.yAxis.tickLine}
            axisLine={
              hideYAxis ? false
                : _base_linechart_ui_configs.yAxis.axisLine
                  ? { stroke: _base_linechart_ui_configs.yAxis.axisLineStroke }
                  : false
            }
            padding={_base_linechart_ui_configs.yAxis.padding}
            ticks={hideYAxis ? [] : yTicks}
            allowDecimals={false}
            width={hideYAxis ? 0 : _base_linechart_ui_configs.yAxis.width}
            fontFamily={TICK_FONT_FAMILY}
            fontWeight={500}
            stroke={_base_linechart_ui_configs.yAxis.tickFill}
            tickFormatter={tickFormatter}
          />

          {/* Tooltip is always rendered; mouse tracking & visibility handled internally */}
          <Tooltip
            content={tooltipContentElement}
            cursor={{ strokeDasharray: "3 3" }}
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ pointerEvents: "none" }}
          />

          {/* Pre-computed line elements (sorted with hovered line on top) */}
          {lineElements}
        </RechartsLineChart>
      </ResponsiveContainer>
    </LineChartWrapper>
  );
};

/**
 * LineChart Working Explanation
 * ============================
 *
 * 1. DATA FLOW:
 *    Raw data → usePrepareLineData (with auto-detection) → Render
 *
 * 2. AUTO-DETECTION:
 *    - Automatically detects x-axis field and value fields based on data structure
 *    - Uses similar logic to the BarChart component
 *
 * 3. RENDERING:
 *    - Chart starts at (0,0) with axes overlapping
 *    - Evenly distributed grid lines with consistent step values (5, 10, 15, etc.)
 *    - Built-in Legend from Recharts for line identification
 *    - CustomTooltip for consistent styling with BarChart component
 *
 * 4. GRID CUSTOMIZATION:
 *    - Nice round numbers for tick values (5, 10, 25, etc.)
 *    - Customizable grid density via props
 *    - The top tick is guaranteed to be at or slightly above the maximum data value
 *
 * 5. INTERACTIONS:
 *    - Mouse tracking for tooltips
 *    - Active point highlighting
 *    - Click handlers for data selection
 */
